// File: src/lib/axios.js
import axios from "axios";

/* ──────────────────────────────────────────────────────────────
 * Runtime helpers
 * ────────────────────────────────────────────────────────────── */

const w =
  typeof window !== "undefined"
    ? window
    : undefined;

const nowHost = (() => {
  try {
    return w?.location?.hostname || "";
  } catch {
    return "";
  }
})();

const nowOrigin = (() => {
  try {
    return w?.location?.origin || "";
  } catch {
    return "";
  }
})();

const sleep = (ms) =>
  new Promise((resolve) =>
    setTimeout(resolve, ms)
  );

const isFormData = (value) =>
  typeof FormData !== "undefined" &&
  value instanceof FormData;

const norm = (value) =>
  String(value || "")
    .replace(/\/+$/, "");

/* ──────────────────────────────────────────────────────────────
 * Retry policy
 *
 * Retry only transient failures:
 * - no HTTP response
 * - timeout
 * - 5xx except 501
 * ────────────────────────────────────────────────────────────── */

function shouldRetry(error) {
  const status =
    error?.response?.status;

  const code =
    error?.code;

  return (
    !status ||
    code === "ECONNABORTED" ||
    (
      status >= 500 &&
      status !== 501
    )
  );
}

/* ──────────────────────────────────────────────────────────────
 * Legacy browser context cleanup
 *
 * IMPORTANT:
 *
 * These values may remain in browsers from older deployments.
 *
 * They are now UX compatibility data only.
 *
 * axios NEVER reads them to:
 * - authenticate
 * - authorize
 * - select a company
 * - select a tenant
 * - establish platform-admin context
 * ────────────────────────────────────────────────────────────── */

const CURRENT_UNIT_KEY =
  "solo:current-unit";

const UNIT_SLUG_KEY =
  "unit:slug";

const LEGACY_UNIT_ID_KEY =
  "activeUnitId";

const LEGACY_UNIT_SLUG_KEY =
  "activeUnitSlug";

const LEGACY_ADMIN_CONTEXT_KEY =
  "solo:admin-context";

/**
 * Clear old browser unit mirrors.
 *
 * Server cookies/session remain authoritative.
 */
export function clearUnitContext() {
  try {
    w?.localStorage?.removeItem(
      CURRENT_UNIT_KEY
    );

    w?.localStorage?.removeItem(
      UNIT_SLUG_KEY
    );

    w?.localStorage?.removeItem(
      LEGACY_UNIT_ID_KEY
    );

    w?.localStorage?.removeItem(
      LEGACY_UNIT_SLUG_KEY
    );
  } catch {
    // noop
  }
}

/* ──────────────────────────────────────────────────────────────
 * Deprecated platform-admin context helpers
 *
 * These exports remain temporarily so existing Sidebar imports
 * do not break before File 9 is regenerated.
 *
 * SECURITY:
 *
 * They deliberately CANNOT provide tenant authority.
 *
 * Real platform context lives only in the signed HttpOnly
 * admin_ctx cookie managed through /admin/context.
 * ────────────────────────────────────────────────────────────── */

export function getAdminContext() {
  return null;
}

export function setAdminContext(_next) {
  /*
   * Intentionally ignored.
   *
   * Browser storage must never establish platform-admin authority.
   */
  try {
    w?.localStorage?.removeItem(
      LEGACY_ADMIN_CONTEXT_KEY
    );
  } catch {
    // noop
  }
}

export function clearAdminContext() {
  /*
   * This clears only historical browser data.
   *
   * To actually clear server-side admin context, callers must use:
   *
   *   DELETE /solo-mdsh-api/admin/context
   */
  try {
    w?.localStorage?.removeItem(
      LEGACY_ADMIN_CONTEXT_KEY
    );
  } catch {
    // noop
  }
}

/* ──────────────────────────────────────────────────────────────
 * Base URLs
 * ────────────────────────────────────────────────────────────── */

const DEV_API =
  "http://127.0.0.1:4000/solo-mdsh-api";

const apiBaseEnv =
  norm(
    import.meta.env
      .VITE_API_BASE_URL
  );

let API_BASE;

if (apiBaseEnv) {
  API_BASE =
    apiBaseEnv;
} else if (
  /^(?:www\.)?solomdhs\.com$/i.test(
    nowHost
  )
) {
  API_BASE =
    "https://api.solomdhs.com/solo-mdsh-api";
} else if (
  /^(localhost|127\.0\.0\.1)$/i.test(
    nowHost
  )
) {
  API_BASE =
    DEV_API;
} else {
  API_BASE =
    `${nowOrigin}/solo-mdsh-api`;
}

const AUTH_BASE =
  norm(
    import.meta.env
      .VITE_AUTH_BASE_URL ||
      `${API_BASE}/auth`
  );

const API_PREFIX = (() => {
  try {
    return (
      new URL(API_BASE)
        .pathname
        .replace(/\/+$/, "") ||
      "/solo-mdsh-api"
    );
  } catch {
    return "/solo-mdsh-api";
  }
})();

const AUTH_PREFIX = (() => {
  try {
    return (
      new URL(AUTH_BASE)
        .pathname
        .replace(/\/+$/, "") ||
      "/solo-mdsh-api/auth"
    );
  } catch {
    return "/solo-mdsh-api/auth";
  }
})();

/* ──────────────────────────────────────────────────────────────
 * Axios instances
 *
 * Cookies are the primary authentication/session transport.
 * ────────────────────────────────────────────────────────────── */

const validateApiStatus =
  (status) =>
    (
      status >= 200 &&
      status < 300
    ) ||
    status === 304;

export const api =
  axios.create({
    baseURL:
      API_BASE,

    withCredentials:
      true,

    timeout:
      20000,

    headers: {
      Accept:
        "application/json",
    },

    validateStatus:
      validateApiStatus,
  });

export const authApi =
  axios.create({
    baseURL:
      AUTH_BASE,

    withCredentials:
      true,

    timeout:
      15000,

    headers: {
      Accept:
        "application/json",
    },

    validateStatus:
      (status) =>
        status >= 200 &&
        status < 300,
  });

/* ──────────────────────────────────────────────────────────────
 * URL prefix normalization
 *
 * Prevent:
 *
 * /solo-mdsh-api/solo-mdsh-api/foo
 *
 * when older callers pass the API prefix themselves.
 * ────────────────────────────────────────────────────────────── */

function stripDuplicatePrefix(
  prefix
) {
  return (config) => {
    const url =
      String(
        config.url || ""
      );

    if (!url) {
      return config;
    }

    if (
      /^https?:\/\//i.test(
        url
      )
    ) {
      return config;
    }

    const asPath =
      url.startsWith("/")
        ? url
        : `/${url}`;

    if (
      prefix !== "/" &&
      asPath.startsWith(
        prefix
      )
    ) {
      const stripped =
        asPath.slice(
          prefix.length
        ) || "/";

      config.url =
        stripped.startsWith("/")
          ? stripped
          : `/${stripped}`;
    }

    return config;
  };
}

/* ──────────────────────────────────────────────────────────────
 * Safe request preparation
 *
 * There is intentionally NO:
 *
 * - localStorage token reading
 * - JWT decoding
 * - Authorization header construction
 * - company query injection
 * - unit query injection
 * - X-Admin-* header injection
 *
 * Tenant/session authority is server-side.
 * ────────────────────────────────────────────────────────────── */

function prepareRequest(
  config
) {
  config.headers =
    config.headers || {};

  /*
   * Let the browser set multipart boundary correctly.
   */
  if (
    isFormData(
      config.data
    )
  ) {
    delete config.headers[
      "Content-Type"
    ];
  }

  return config;
}

/* ──────────────────────────────────────────────────────────────
 * Request interceptors
 * ────────────────────────────────────────────────────────────── */

api.interceptors.request.use(
  stripDuplicatePrefix(
    API_PREFIX
  )
);

authApi.interceptors.request.use(
  stripDuplicatePrefix(
    AUTH_PREFIX
  )
);

api.interceptors.request.use(
  prepareRequest
);

authApi.interceptors.request.use(
  prepareRequest
);

/* ──────────────────────────────────────────────────────────────
 * Browser auth events
 *
 * These are UI coordination only.
 * They never grant authorization.
 * ────────────────────────────────────────────────────────────── */

function dispatchAuthEvent(
  name,
  detail
) {
  try {
    w?.dispatchEvent?.(
      new CustomEvent(
        name,
        {
          detail,
        }
      )
    );
  } catch {
    // noop
  }
}

/* ──────────────────────────────────────────────────────────────
 * Platform-admin denial handling
 * ────────────────────────────────────────────────────────────── */

function isPlatformAdminDeniedResponse(
  error
) {
  const status =
    error?.response?.status;

  if (status !== 403) {
    return false;
  }

  const data =
    error?.response?.data ||
    {};

  const code =
    String(
      data?.error ||
      data?.code ||
      data?.message ||
      ""
    )
      .trim()
      .toUpperCase();

  return (
    code ===
      "PLATFORM_ADMIN_REQUIRED" ||
    code.includes(
      "PLATFORM_ADMIN_REQUIRED"
    )
  );
}

function createPlatformAdminDeniedDispatcher() {
  let lastAt = 0;

  const MIN_GAP_MS =
    1500;

  return (detail) => {
    const current =
      Date.now();

    if (
      current - lastAt <
      MIN_GAP_MS
    ) {
      return;
    }

    lastAt =
      current;

    /*
     * Remove any historical localStorage mirror.
     *
     * Actual signed admin_ctx remains server-controlled.
     */
    clearAdminContext();

    dispatchAuthEvent(
      "auth:platform-admin-denied",
      detail
    );

    dispatchAuthEvent(
      "auth:go-dashboard",
      {
        reason:
          "platform-admin-denied",
      }
    );
  };
}

const firePlatformAdminDeniedOnce =
  createPlatformAdminDeniedDispatcher();

/* ──────────────────────────────────────────────────────────────
 * 401 handling
 *
 * Do NOT globally log out because one ordinary business endpoint
 * happens to return 401.
 *
 * Server-authoritative session endpoints decide whether the
 * browser session is invalid.
 * ────────────────────────────────────────────────────────────── */

function shouldForceLogout(
  error
) {
  const status =
    error?.response?.status;

  if (status !== 401) {
    return false;
  }

  const config =
    error?.config || {};

  const base =
    String(
      config.baseURL || ""
    );

  const url =
    String(
      config.url || ""
    );

  let pathname = "";

  try {
    pathname =
      new URL(
        url,
        base ||
        nowOrigin ||
        "http://localhost"
      ).pathname;
  } catch {
    pathname =
      `${base}${url}`;
  }

  return (
    pathname.endsWith(
      "/auth/me"
    ) ||
    pathname.includes(
      "/auth/me?"
    ) ||
    pathname.endsWith(
      "/auth/refresh"
    ) ||
    pathname.includes(
      "/auth/refresh?"
    )
  );
}

function create401Handler() {
  let bouncing =
    false;

  return (error) => {
    if (
      shouldForceLogout(
        error
      ) &&
      !bouncing
    ) {
      bouncing =
        true;

      dispatchAuthEvent(
        "auth:force-logout"
      );

      setTimeout(
        () => {
          bouncing =
            false;
        },
        250
      );
    }

    return Promise.reject(
      error
    );
  };
}

const on401 =
  create401Handler();

/* ──────────────────────────────────────────────────────────────
 * Primary response handlers
 * ────────────────────────────────────────────────────────────── */

api.interceptors.response.use(
  (response) =>
    response,

  (error) => {
    if (
      isPlatformAdminDeniedResponse(
        error
      )
    ) {
      const config =
        error?.config || {};

      firePlatformAdminDeniedOnce({
        status:
          403,

        url:
          `${String(
            config.baseURL ||
            ""
          )}${String(
            config.url ||
            ""
          )}`,

        data:
          error?.response?.data ||
          null,
      });

      return Promise.reject(
        error
      );
    }

    return on401(
      error
    );
  }
);

authApi.interceptors.response.use(
  (response) =>
    response,

  on401
);

/* ──────────────────────────────────────────────────────────────
 * Auth workflow bridge
 *
 * Current login still supports multi-unit selection.
 * ────────────────────────────────────────────────────────────── */

authApi.interceptors.response.use(
  (response) =>
    response,

  (error) => {
    try {
      const status =
        error?.response?.status;

      const data =
        error?.response?.data;

      if (
        status === 409 &&
        data?.error ===
          "UNIT_REQUIRED"
      ) {
        dispatchAuthEvent(
          "auth:unit-required",
          {
            units:
              Array.isArray(
                data?.units
              )
                ? data.units
                : [],

            message:
              data?.message ||
              "",
          }
        );
      }

      if (
        status === 400 &&
        data?.error ===
          "INVALID_UNIT"
      ) {
        /*
         * Clear only browser UX mirrors.
         *
         * Backend remains authoritative.
         */
        clearUnitContext();

        dispatchAuthEvent(
          "auth:invalid-unit",
          {
            units:
              Array.isArray(
                data?.units
              )
                ? data.units
                : [],

            message:
              data?.message ||
              "",
          }
        );
      }
    } catch {
      // noop
    }

    return Promise.reject(
      error
    );
  }
);

/* ──────────────────────────────────────────────────────────────
 * Bounded retry
 *
 * Retry through the SAME axios instance so instance configuration,
 * credentials and interceptors remain intact.
 * ────────────────────────────────────────────────────────────── */

function installRetry(
  client,
  maxRetries = 2
) {
  client.interceptors.response.use(
    (response) =>
      response,

    async (error) => {
      const config =
        error?.config;

      if (!config) {
        return Promise.reject(
          error
        );
      }

      config.__retryCount =
        config.__retryCount ||
        0;

      /*
       * Do not retry authorization failures.
       */
      if (
        isPlatformAdminDeniedResponse(
          error
        )
      ) {
        return Promise.reject(
          error
        );
      }

      /*
       * Authentication operations should not be replayed automatically.
       *
       * login/password reset/logout may have side effects or confusing UX.
       */
      let pathname = "";

      try {
        pathname =
          new URL(
            config.url || "",
            config.baseURL ||
            nowOrigin ||
            "http://localhost"
          ).pathname;
      } catch {
        pathname =
          String(
            config.url || ""
          );
      }

      const isAuthRequest =
        /\/auth(?:\/|$)/i.test(
          pathname
        );

      if (
        !isAuthRequest &&
        config.__retryCount <
          maxRetries &&
        shouldRetry(error)
      ) {
        config.__retryCount +=
          1;

        await sleep(
          250 *
            config.__retryCount
        );

        return client.request(
          config
        );
      }

      return Promise.reject(
        error
      );
    }
  );
}

installRetry(
  api,
  2
);

installRetry(
  authApi,
  0
);

/* ──────────────────────────────────────────────────────────────
 * Development logging
 * ────────────────────────────────────────────────────────────── */

if (import.meta.env.DEV) {
  function toURL(config) {
    try {
      const url =
        new URL(
          config.url || "",
          config.baseURL ||
          nowOrigin ||
          "http://localhost"
        );

      if (config.params) {
        for (
          const [key, value]
          of Object.entries(
            config.params
          )
        ) {
          if (
            value !== null &&
            value !== undefined
          ) {
            url.searchParams.set(
              key,
              value
            );
          }
        }
      }

      return url.toString();
    } catch {
      return (
        config.url ||
        ""
      );
    }
  }

  for (
    const client
    of [
      api,
      authApi,
    ]
  ) {
    client.interceptors.request.use(
      (config) => {
        try {
          console.debug(
            `[${
              client === api
                ? "api"
                : "auth"
            }] →`,
            (
              config.method ||
              "get"
            ).toUpperCase(),
            toURL(config)
          );
        } catch {
          // noop
        }

        return config;
      }
    );

    client.interceptors.response.use(
      (response) =>
        response,

      (error) => {
        try {
          const config =
            error?.config ||
            {};

          console.debug(
            `[${
              client === api
                ? "api"
                : "auth"
            }] ✖`,
            (
              config.method ||
              "get"
            ).toUpperCase(),
            toURL(config),
            error?.response
              ?.status,
            error?.response
              ?.data
          );
        } catch {
          // noop
        }

        return Promise.reject(
          error
        );
      }
    );
  }
}

export default api;